import type { TourId, UiInputBindingId } from '@sorrem/shared';
import type { TabDef } from '@sorrem-client/ui/components';

export type CharacterHomeTab = 'overview' | 'inventory' | 'skills' | 'crucibles' | 'crafting' | 'journey';

/** One Character Home tab: its label, the key binding that opens it and its first-visit tour. */
export interface CharacterHomeTabDefinition {
    readonly label: string;
    readonly binding: UiInputBindingId;
    readonly tourId: TourId;
}

/**
 * Every Character Home tab, in display order. The tab strip, the tab hotkeys,
 * the route's tab check and the tab tours all derive from this table, so a new
 * tab must be described here once.
 */
export const CHARACTER_HOME_TABS: Readonly<Record<CharacterHomeTab, CharacterHomeTabDefinition>> = {
    // Overview reuses O, the in-run commission key; Inventory reuses I, the in-run Carried panel key.
    overview: { label: 'Overview', binding: 'toggle-commission', tourId: 'tab-overview' },
    inventory: { label: 'Inventory', binding: 'toggle-inventory', tourId: 'tab-inventory' },
    skills: { label: 'Skills', binding: 'open-skills', tourId: 'tab-skills' },
    crucibles: { label: 'Crucibles', binding: 'open-crucibles', tourId: 'tab-crucibles' },
    crafting: { label: 'Crafting', binding: 'open-crafting', tourId: 'tab-crafting' },
    journey: { label: 'Journey', binding: 'open-journey', tourId: 'tab-journey' },
};

/** The tab ids in display order. */
export const CHARACTER_HOME_TAB_IDS = Object.keys(CHARACTER_HOME_TABS) as readonly CharacterHomeTab[];

/** The tab strip's entries. */
export const CHARACTER_HOME_TAB_LIST: TabDef<CharacterHomeTab>[] = CHARACTER_HOME_TAB_IDS.map((id) => ({
    id,
    label: CHARACTER_HOME_TABS[id].label,
}));

/** Whether a route segment names a Character Home tab. */
export function isCharacterHomeTab(value: string | undefined): value is CharacterHomeTab {
    return value !== undefined && Object.hasOwn(CHARACTER_HOME_TABS, value);
}
